import React, { useCallback, useEffect, useRef, useState } from "react";

import {
  IconCheck,
  IconRefreshCw,
  IconSearch,
  IconXMark,
} from "@frontend/common";

import { useGetTerritoryControlledListQuery } from "@api/territoryControlledList/territoryControlledList";

import * as S from "./DealLaScTerritoryControlledModal.style";

type TreeNode = {
  code: string;
  label: string;
  count?: number;
  children?: TreeNode[];
};

type GroupNode = {
  code: string;
  label: string;
  countryList: string[];
};

type CheckState = "checked" | "indeterminate" | "unchecked";

function getAllLeafCodes(node: TreeNode): string[] {
  if (!node.children || node.children.length === 0) return [node.code];
  return node.children.flatMap(getAllLeafCodes);
}

function getCheckState(node: TreeNode, selected: Set<string>): CheckState {
  if (!node.children || node.children.length === 0) {
    return selected.has(node.code) ? "checked" : "unchecked";
  }
  const leaves = getAllLeafCodes(node);
  const count = leaves.filter((c) => selected.has(c)).length;
  if (count === 0) return "unchecked";
  if (count === leaves.length) return "checked";
  return "indeterminate";
}

function expandCodesToLeaves(
  codes: string[],
  nodes: TreeNode[],
  groups: GroupNode[] = [],
): Set<string> {
  const findNode = (code: string, nodeList: TreeNode[]): TreeNode | null =>
    nodeList.reduce<TreeNode | null>(
      (found, node) =>
        found ??
        (node.code === code ? node : findNode(code, node.children ?? [])),
      null,
    );

  return codes.reduce<Set<string>>((acc, code) => {
    const treeNode = findNode(code, nodes);
    if (treeNode) {
      getAllLeafCodes(treeNode).forEach((c) => acc.add(c));
      return acc;
    }

    const group = groups.find((g) => g.code === code);
    if (group) {
      group.countryList.forEach((c) => acc.add(c));
      return acc;
    }

    acc.add(code);
    return acc;
  }, new Set<string>());
}

function compressSelection(
  selected: Set<string>,
  wwNode: TreeNode,
  groups: GroupNode[],
): string[] {
  if (selected.size === 0) return [];

  const allLeaves = getAllLeafCodes(wwNode);

  // 1. WW — all countries selected
  if (allLeaves.length > 0 && allLeaves.every((c) => selected.has(c)))
    return [wwNode.code];

  const result: string[] = [];
  const accounted = new Set<string>();

  // 2. Full continents
  (wwNode.children ?? []).forEach((continent) => {
    const contLeaves = getAllLeafCodes(continent);
    if (contLeaves.length > 0 && contLeaves.every((c) => selected.has(c))) {
      result.push(continent.code);
      contLeaves.forEach((c) => accounted.add(c));
    }
  });

  // 3. Groups — cover unaccounted countries, removing subsets covered by larger groups
  const selectedGroups = groups.filter(
    (g) =>
      g.countryList.length > 0 &&
      g.countryList.every((c) => selected.has(c)) &&
      g.countryList.some((c) => !accounted.has(c)),
  );
  const nonRedundantGroups = selectedGroups.filter(
    (g) =>
      !selectedGroups.some(
        (other) =>
          other.code !== g.code &&
          g.countryList.every((c) => other.countryList.includes(c)),
      ),
  );
  nonRedundantGroups.forEach((group) => {
    result.push(group.code);
    group.countryList.forEach((c) => accounted.add(c));
  });

  // 4–5. Regions and countries for partially-selected continents
  (wwNode.children ?? []).forEach((continent) => {
    const contLeaves = getAllLeafCodes(continent);
    if (contLeaves.every((c) => accounted.has(c))) return;

    (continent.children ?? []).forEach((region) => {
      const regLeaves = getAllLeafCodes(region).filter(
        (c) => !accounted.has(c),
      );
      if (regLeaves.length === 0) return;

      if (regLeaves.every((c) => selected.has(c))) {
        // 4. Full region (of unaccounted leaves)
        result.push(region.code);
        regLeaves.forEach((c) => accounted.add(c));
      } else {
        // 5. Individual countries
        (region.children ?? []).forEach((country) => {
          if (!accounted.has(country.code) && selected.has(country.code)) {
            result.push(country.code);
            accounted.add(country.code);
          }
        });
      }
    });
  });

  return result;
}

function getGroupCheckState(
  group: GroupNode,
  selected: Set<string>,
): CheckState {
  const count = group.countryList.filter((c) => selected.has(c)).length;
  if (count === 0) return "unchecked";
  if (count === group.countryList.length) return "checked";
  return "indeterminate";
}

type Props = {
  onClose: () => void;
  selectedInclude: string[];
  selectedExclude: string[];
  saveSelectedData: (
    selectedInclude: string[],
    selectedExclude: string[],
  ) => void;
};

const DealLaScTerritoryControlledModal = ({
  onClose,
  selectedInclude,
  selectedExclude,
  saveSelectedData,
}: Props) => {
  const { data: territoryControlledList } =
    useGetTerritoryControlledListQuery();
  const [includeSelected, setIncludeSelected] = useState<Set<string>>(
    new Set(),
  );
  const [excludeSelected, setExcludeSelected] = useState<Set<string>>(
    new Set(),
  );
  const hasInitialized = useRef(false);
  const initialInclude = useRef(selectedInclude);
  const initialExclude = useRef(selectedExclude);
  const initialIncludeLeaves = useRef<Set<string>>(new Set());
  const initialExcludeLeaves = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!territoryControlledList || hasInitialized.current) return;
    hasInitialized.current = true;
    const includeLeaves = expandCodesToLeaves(
      initialInclude.current,
      territoryControlledList.countryInformationList,
      territoryControlledList.groupInformationList,
    );
    const excludeLeaves = expandCodesToLeaves(
      initialExclude.current,
      territoryControlledList.countryInformationList,
      territoryControlledList.groupInformationList,
    );
    initialIncludeLeaves.current = includeLeaves;
    initialExcludeLeaves.current = excludeLeaves;
    setIncludeSelected(includeLeaves);
    setExcludeSelected(excludeLeaves);
  }, [territoryControlledList]);

  const setsEqual = (a: Set<string>, b: Set<string>) =>
    a.size === b.size && [...a].every((c) => b.has(c));

  const isApplyDisabled =
    setsEqual(includeSelected, initialIncludeLeaves.current) &&
    setsEqual(excludeSelected, initialExcludeLeaves.current);
  const [includeExpandedCodes, setIncludeExpandedCodes] = useState<Set<string>>(
    new Set(),
  );
  const [excludeExpandedCodes, setExcludeExpandedCodes] = useState<Set<string>>(
    new Set(),
  );
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    if (!territoryControlledList) return;
    if (!searchQuery) {
      setIncludeExpandedCodes(new Set());
      setExcludeExpandedCodes(new Set());
      return;
    }
    const q = searchQuery.toLowerCase();
    const nodeHasMatch = (node: TreeNode): boolean => {
      if (
        node.label.toLowerCase().includes(q) ||
        node.code.toLowerCase().includes(q)
      )
        return true;
      if (node.children) return node.children.some(nodeHasMatch);
      return false;
    };
    const childHasMatch = (node: TreeNode): boolean =>
      (node.children ?? []).some(nodeHasMatch);
    const codesToExpand = new Set<string>();
    const collect = (node: TreeNode) => {
      if (!node.children || node.children.length === 0) return;
      if (childHasMatch(node)) codesToExpand.add(node.code);
      node.children.forEach(collect);
    };
    const ww = territoryControlledList.countryInformationList[0];
    (ww.children ?? []).forEach(collect);
    setIncludeExpandedCodes(new Set(codesToExpand));
    setExcludeExpandedCodes(new Set(codesToExpand));
  }, [searchQuery, territoryControlledList]);

  const toggleExpand = useCallback(
    (
      code: string,
      setExpandedCodes: React.Dispatch<React.SetStateAction<Set<string>>>,
    ) => {
      setExpandedCodes((prev) => {
        const next = new Set(prev);
        if (next.has(code)) next.delete(code);
        else next.add(code);
        return next;
      });
    },
    [],
  );

  const toggleNode = useCallback(
    (
      node: TreeNode,
      selected: Set<string>,
      setSelected: React.Dispatch<React.SetStateAction<Set<string>>>,
    ) => {
      const leaves = getAllLeafCodes(node);
      const state = getCheckState(node, selected);
      setSelected((prev) => {
        const next = new Set(prev);
        if (state === "checked") {
          leaves.forEach((c) => next.delete(c));
        } else {
          leaves.forEach((c) => next.add(c));
        }
        return next;
      });
    },
    [],
  );

  const toggleGroup = useCallback(
    (
      group: GroupNode,
      selected: Set<string>,
      setSelected: React.Dispatch<React.SetStateAction<Set<string>>>,
    ) => {
      const state = getGroupCheckState(group, selected);
      setSelected((prev) => {
        const next = new Set(prev);
        if (state === "checked") {
          group.countryList.forEach((c) => next.delete(c));
        } else {
          group.countryList.forEach((c) => next.add(c));
        }
        return next;
      });
    },
    [],
  );

  const matchesSearch = (node: TreeNode, query: string): boolean => {
    if (!query) return true;
    const q = query.toLowerCase();
    if (node.label.toLowerCase().includes(q)) return true;
    if (node.code.toLowerCase().includes(q)) return true;
    if (node.children) return node.children.some((c) => matchesSearch(c, q));
    return false;
  };

  const matchesDirect = (node: TreeNode, query: string): boolean => {
    if (!query) return true;
    const q = query.toLowerCase();
    return (
      node.label.toLowerCase().includes(q) ||
      node.code.toLowerCase().includes(q)
    );
  };

  const renderTree = (
    node: TreeNode,
    depth: number,
    selected: Set<string>,
    setSelected: React.Dispatch<React.SetStateAction<Set<string>>>,
    expandedCodes: Set<string>,
    setExpandedCodes: React.Dispatch<React.SetStateAction<Set<string>>>,
    forceShow = false,
  ): React.ReactNode => {
    if (!forceShow && !matchesSearch(node, searchQuery)) return null;

    const isLeaf = !node.children || node.children.length === 0;
    const checkState = getCheckState(node, selected);
    const isExpanded = expandedCodes.has(node.code);
    const showAllChildren = forceShow || matchesDirect(node, searchQuery);

    return (
      <React.Fragment key={`${node.code}-${depth}`}>
        <S.TreeItem $depth={depth}>
          {!isLeaf ? (
            <S.ExpandButton
              type="button"
              onClick={() => toggleExpand(node.code, setExpandedCodes)}
            >
              <S.ArrowIcon $expanded={isExpanded} />
            </S.ExpandButton>
          ) : (
            <S.ExpandSpacer />
          )}
          <S.CheckboxEl
            $state={checkState}
            onClick={() => toggleNode(node, selected, setSelected)}
          />
          <S.ItemLabel onClick={() => toggleNode(node, selected, setSelected)}>
            {node.label} ({node.code})
          </S.ItemLabel>
          {node.count !== undefined && <S.ItemCount>{node.count}</S.ItemCount>}
        </S.TreeItem>
        {!isLeaf &&
          isExpanded &&
          node.children?.map((child) =>
            renderTree(
              child,
              depth + 1,
              selected,
              setSelected,
              expandedCodes,
              setExpandedCodes,
              showAllChildren,
            ),
          )}
      </React.Fragment>
    );
  };

  const wwNode = territoryControlledList.countryInformationList[0];
  const includeCount = includeSelected.size;
  const excludeCount = excludeSelected.size;

  const renderColumn = (
    title: "Include" | "Exclude",
    selected: Set<string>,
    setSelected: React.Dispatch<React.SetStateAction<Set<string>>>,
    expandedCodes: Set<string>,
    setExpandedCodes: React.Dispatch<React.SetStateAction<Set<string>>>,
  ) => (
    <S.Column>
      <S.ColumnHeader>
        <S.ColumnTitle>{title}</S.ColumnTitle>
        <S.ClearAllButton
          type="button"
          disabled={selected.size === 0}
          onClick={() => setSelected(new Set())}
        >
          <IconRefreshCw width={16} height={16} color="#E53E06" />
          Clear all
        </S.ClearAllButton>
      </S.ColumnHeader>
      <S.TreeWrapper>
        <S.TreeList>
          {(!searchQuery || matchesSearch(wwNode, searchQuery)) && (
            <S.TreeItem $depth={0}>
              <S.CheckboxEl
                $state={getCheckState(wwNode, selected)}
                onClick={() => toggleNode(wwNode, selected, setSelected)}
              />
              <S.ItemLabel
                onClick={() => toggleNode(wwNode, selected, setSelected)}
              >
                {wwNode.label} ({wwNode.code})
              </S.ItemLabel>
              {wwNode.count !== undefined && (
                <S.ItemCount>{wwNode.count}</S.ItemCount>
              )}
            </S.TreeItem>
          )}
          {wwNode.children?.map((continent: TreeNode) =>
            renderTree(
              continent,
              0,
              selected,
              setSelected,
              expandedCodes,
              setExpandedCodes,
            ),
          )}
          {(() => {
            const visibleGroups =
              territoryControlledList.groupInformationList.filter(
                (group: GroupNode) =>
                  !searchQuery ||
                  group.label
                    .toLowerCase()
                    .includes(searchQuery.toLowerCase()) ||
                  group.code.toLowerCase().includes(searchQuery.toLowerCase()),
              );
            if (visibleGroups.length === 0) return null;
            return (
              <>
                <S.RowDivider />
                <S.GroupsLabel>Groups & Blocs</S.GroupsLabel>
              </>
            );
          })()}
          {territoryControlledList.groupInformationList.map(
            (group: GroupNode) => {
              if (
                searchQuery &&
                !group.label
                  .toLowerCase()
                  .includes(searchQuery.toLowerCase()) &&
                !group.code.toLowerCase().includes(searchQuery.toLowerCase())
              )
                return null;
              const state = getGroupCheckState(group, selected);
              return (
                <S.TreeItem key={group.code} $depth={0}>
                  <S.ExpandSpacer />
                  <S.CheckboxEl
                    $state={state}
                    onClick={() => toggleGroup(group, selected, setSelected)}
                  />
                  <S.ItemLabel
                    onClick={() => toggleGroup(group, selected, setSelected)}
                  >
                    {group.label} ({group.code})
                  </S.ItemLabel>
                </S.TreeItem>
              );
            },
          )}
        </S.TreeList>
      </S.TreeWrapper>
    </S.Column>
  );

  return (
    <S.Wrapper>
      <S.ContentsWrapper>
        <S.Header>
          <S.Title>Territory Normalized</S.Title>
          <S.CloseButton type="button" onClick={onClose}>
            <IconXMark width={24} height={24} color="#000000" />
          </S.CloseButton>
        </S.Header>
        <S.SearchAndValueWrapper>
          <S.SearchWrapper>
            <S.SearchIconWrapper>
              <IconSearch width={24} height={24} color="#515C6C" />
            </S.SearchIconWrapper>
            <S.SearchInput
              placeholder="Search continent, region, country, or group"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <S.SearchClearButton
                type="button"
                onClick={() => setSearchQuery("")}
              >
                <IconXMark width={22} height={22} color="#515C6C" />
              </S.SearchClearButton>
            )}
          </S.SearchWrapper>
          <S.ColumnsWrapper>
            {renderColumn(
              "Include",
              includeSelected,
              setIncludeSelected,
              includeExpandedCodes,
              setIncludeExpandedCodes,
            )}
            <S.ColumnDivider />
            {renderColumn(
              "Exclude",
              excludeSelected,
              setExcludeSelected,
              excludeExpandedCodes,
              setExcludeExpandedCodes,
            )}
          </S.ColumnsWrapper>
        </S.SearchAndValueWrapper>
        <S.Footer>
          <S.CountText>
            Include: {includeCount} countries | Exclude: {excludeCount}{" "}
            countries
          </S.CountText>
          <S.FooterButtons>
            <S.CancelButton type="button" onClick={onClose}>
              Cancel
            </S.CancelButton>
            <S.ApplyButton
              type="button"
              disabled={isApplyDisabled}
              onClick={() => {
                saveSelectedData(
                  compressSelection(
                    includeSelected,
                    wwNode,
                    territoryControlledList.groupInformationList,
                  ),
                  compressSelection(
                    excludeSelected,
                    wwNode,
                    territoryControlledList.groupInformationList,
                  ),
                );
                onClose();
              }}
            >
              Apply <IconCheck width={20} height={20} color="#FFFFFF" />
            </S.ApplyButton>
          </S.FooterButtons>
        </S.Footer>
      </S.ContentsWrapper>
    </S.Wrapper>
  );
};

export default DealLaScTerritoryControlledModal;
